/**
 * The metric registry — one place that defines what every number on the
 * dashboard means, how it should be read, and where clicking it goes.
 *
 * Each entry carries:
 *   label        what the user sees
 *   definition   the plain-language formula, shown in the tile's tooltip. A
 *                metric with no stated definition is a metric nobody trusts.
 *   format       how to render the value (see lib/format.js)
 *   basis        'rate' | 'count' — picks the delta volume floor
 *   direction    whether up is good, bad, or neither
 *   noun         the unit for the denominator line
 *   drill        (payload) => filter object for the Calls page, or null
 *   targetKey    key in preferences.targets, when the metric has one
 *
 * The keys match the `summary` object returned by GET /api/v2/overview.
 */

import { DIRECTION } from './format';

export const METRICS = {
  inbound_answer_rate: {
    label: 'Inbound answer rate',
    short: 'Answer rate',
    definition:
      'Inbound calls marked answered, divided by all inbound calls in the period.',
    format: 'percent',
    basis: 'rate',
    direction: DIRECTION.UP_GOOD,
    noun: 'inbound calls',
    targetKey: 'answer_rate',
    drill: () => ({ inbound: 'true' }),
  },

  missed_inbound: {
    label: 'Unanswered inbound',
    short: 'Unanswered',
    definition:
      'Inbound calls nobody answered. Split into calls that left a voicemail and calls that hung up.',
    format: 'integer',
    basis: 'count',
    direction: DIRECTION.UP_BAD,
    noun: 'calls',
    drill: () => ({ inbound: 'true', answered: 'false' }),
  },

  callback_rate: {
    label: 'Callback rate',
    short: 'Callback rate',
    definition:
      'Distinct phone numbers that missed us and were later reached, divided by all distinct numbers that missed us.',
    format: 'percent',
    basis: 'rate',
    direction: DIRECTION.UP_GOOD,
    noun: 'missed callers',
    targetKey: 'callback_rate',
    drill: null, // handled by the callbacks panel, which owns the call-id set
  },

  median_callback_minutes: {
    label: 'Median time to callback',
    short: 'Callback time',
    definition:
      'Middle value of the time between a missed inbound call and the next answered call with that number.',
    format: 'minutes',
    basis: 'count',
    direction: DIRECTION.UP_BAD,
    noun: 'callbacks',
    targetKey: 'callback_sla_minutes',
    drill: null,
  },

  quality_attainment: {
    label: 'Quality attainment',
    short: 'Quality',
    definition:
      'Average call score as a share of the maximum the scorecard can award. Only calls the AI scored are included.',
    format: 'percent',
    basis: 'rate',
    direction: DIRECTION.UP_GOOD,
    noun: 'scored calls',
    targetKey: 'quality_attainment',
    drill: () => ({ hasQualityScore: 'true' }),
  },

  followup_closure_rate: {
    label: 'Follow-up closure',
    short: 'Closure',
    definition:
      'Follow-ups marked done, divided by all follow-ups raised in the period.',
    format: 'percent',
    basis: 'rate',
    direction: DIRECTION.UP_GOOD,
    noun: 'follow-ups',
    targetKey: 'followup_closure_rate',
    drill: null,
  },

  total_calls: {
    label: 'Total calls',
    short: 'Total calls',
    definition: 'Every call in the period, inbound and outbound, answered or not.',
    format: 'integer',
    basis: 'count',
    // Volume is context, never performance. It gets an arrow and no colour.
    direction: DIRECTION.NEUTRAL,
    noun: 'calls',
    drill: () => ({}),
  },

  outbound_connect_rate: {
    label: 'Outbound connect rate',
    short: 'Connect rate',
    definition:
      'Outbound calls that were answered, divided by all outbound calls in the period.',
    format: 'percent',
    basis: 'rate',
    direction: DIRECTION.UP_GOOD,
    noun: 'outbound calls',
    drill: () => ({ inbound: 'false' }),
  },

  avg_handle_time: {
    label: 'Average handle time',
    short: 'Handle time',
    definition:
      'Mean talk time across answered calls with a recorded duration. Unanswered and zero-length calls are excluded.',
    format: 'durationWords',
    basis: 'count',
    // Inside the band is fine, outside it is worth a look — but longer is not
    // simply worse, so the arrow carries no colour.
    direction: DIRECTION.NEUTRAL,
    noun: 'answered calls',
    targetKey: 'aht_band',
    drill: () => ({ answered: 'true' }),
  },

  negative_rate: {
    label: 'Negative call rate',
    short: 'Negative rate',
    definition:
      'Calls the AI judged negative, divided by all calls it analysed. Unanalysed calls are excluded and reported separately.',
    format: 'percent',
    basis: 'rate',
    direction: DIRECTION.UP_BAD,
    noun: 'analysed calls',
    drill: () => ({ sentiment: 'Negative' }),
  },

  repeat_caller_rate: {
    label: 'Repeat-caller rate',
    short: 'Repeat callers',
    definition:
      'Distinct numbers that called more than once, divided by all distinct numbers in the period.',
    format: 'percent',
    basis: 'rate',
    // Reading depends on the business: for a clinic it means unresolved first
    // calls; for a dealership it means an engaged pipeline.
    direction: DIRECTION.NEUTRAL,
    noun: 'callers',
    drill: null,
  },

  analysis_coverage: {
    label: 'Analysis coverage',
    short: 'Coverage',
    definition:
      'Calls with AI analysis, divided by all calls in the period. Every sentiment and quality figure is a sample of this.',
    format: 'percent',
    basis: 'rate',
    direction: DIRECTION.UP_GOOD,
    noun: 'calls',
    drill: null,
  },

  voicemail_share: {
    label: 'Voicemail share of missed',
    short: 'Voicemail share',
    definition:
      'Unanswered inbound calls that left a voicemail, divided by all unanswered inbound calls. A voicemail is served; a hang-up is lost.',
    format: 'percent',
    basis: 'rate',
    direction: DIRECTION.UP_GOOD,
    noun: 'missed calls',
    drill: null,
  },
};

/** The four headline tiles, in reading order. */
export const PRIMARY_METRICS = [
  'inbound_answer_rate',
  'missed_inbound',
  'callback_rate',
  'quality_attainment',
];

/** The compact secondary strip. */
export const SECONDARY_METRICS = [
  'total_calls',
  'outbound_connect_rate',
  'avg_handle_time',
  'negative_rate',
  'repeat_caller_rate',
  'followup_closure_rate',
];

export const getMetric = (key) => METRICS[key] || null;

/**
 * Default targets by industry. Shown in grey with an "adjust" affordance until
 * the tenant sets their own — never presented as a choice they made.
 * Stored at tenant_settings.other_settings.metric_targets.
 */
export const TARGET_PRESETS = {
  clinic: {
    answer_rate: 0.92,
    callback_rate: 0.9,
    callback_sla_minutes: 30,
    quality_attainment: 0.8,
    followup_closure_rate: 0.85,
    aht_band: [120, 480],
  },
  dealership: {
    answer_rate: 0.85,
    callback_rate: 0.9,
    callback_sla_minutes: 15,
    quality_attainment: 0.75,
    followup_closure_rate: 0.8,
    aht_band: [120, 600],
  },
  services: {
    answer_rate: 0.8,
    callback_rate: 0.85,
    callback_sla_minutes: 60,
    quality_attainment: 0.75,
    followup_closure_rate: 0.8,
    aht_band: [90, 480],
  },
  default: {
    answer_rate: 0.85,
    callback_rate: 0.85,
    callback_sla_minutes: 30,
    quality_attainment: 0.75,
    followup_closure_rate: 0.8,
    aht_band: [120, 480],
  },
};

export const DEFAULT_TARGETS = TARGET_PRESETS.default;

/** Insight severity ranks — used to order the "What changed" strip. */
export const INSIGHT_SEVERITY = { critical: 3, warning: 2, info: 1 };

export const insightTone = (severity) => {
  switch (severity) {
    case 'critical': return 'bad';
    case 'warning': return 'warn';
    default: return 'info';
  }
};
